import React from 'react';

interface Props {
  /** Title of the first change to make, or null when nothing needs changing. */
  firstChange: string | null;
  onNewAnalysis: () => void;
}

/**
 * The "Remind me in 7 days" and "Email me when it lands" CTAs from the design
 * are deliberately absent — neither has a backend (SHU-100, SHU-99). A dead
 * button is worse than no button, so the honest note ships as static copy.
 */
const FitNextSteps: React.FC<Props> = ({ firstChange, onNewAnalysis }) => (
  <div className="fr-rescan">
    <span className="fr-kicker">Your next two weeks</span>
    <h3>Change one thing, ride it, film it again.</h3>
    <p>A fit isn't a document you file away — it's a loop. Here's the version that works.</p>

    <div className="fr-plan">
      <div className="fr-pstep">
        <span className="fr-pstep__n">1</span>
        <span className="fr-pstep__text">
          <b>Today</b>
          {firstChange
            ? `${firstChange}, then ride 20 easy minutes so your body settles into it.`
            : 'Nothing to adjust — keep riding it as it is.'}
        </span>
      </div>
      <div className="fr-pstep">
        <span className="fr-pstep__n">2</span>
        <span className="fr-pstep__text">
          <b>This week — just ride</b>
          Two or three normal rides. Don't touch anything else, and note anything that feels
          different: hands, sit bones, lower back.
        </span>
      </div>
      <div className="fr-pstep">
        <span className="fr-pstep__n">3</span>
        <span className="fr-pstep__text">
          <b>Next weekend — film 30 seconds again</b>
          Same trainer, same camera spot, same shorts. We'll re-measure and tell you whether
          it actually moved.
        </span>
      </div>
    </div>

    <div style={{ marginTop: 20 }}>
      <button type="button" className="fr-btn fr-btn--pri" onClick={onNewAnalysis}>
        Film a follow-up scan
      </button>
    </div>

    <div className="fr-soon">
      <span>
        <b>Being honest about what's built:</b> a follow-up scan comes back as its own fresh
        report. Lining two scans up side by side — so you can watch a number come down — is
        what we're building next.
      </span>
    </div>
  </div>
);

export default FitNextSteps;
